import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Modal from '../Modal/Modal';
import ChevronIcon from '../../assets/svgs/ChevronIcon';
import NegativeIcon from '../../assets/svgs/NegativeIcon';
import PositiveIcon from '../../assets/svgs/PositiveIcon';
import ZoomIcon from '../../assets/svgs/ZoomIcon';
import NoPhotoIcon from '../../assets/svgs/NoPhotoIcon';
import LoadingSection from '../../components/Loading/LoadingSection';

import { useDisableBodyScroll } from '../../utils/useDisableBodyScroll';
import { functions, colors } from '../../ui/variables';
import translate from '../../libs/i18n';
import { NewExamImagesGalleryStyle } from './NewExamImagesGalleryStyle';

const NewExamImagesGallery = ({ images }) => {
  const language = useSelector((state) => state.settings.language);

  const [examImagesModal, setExamImagesModal] = useState(false);
  const [scale, setScale] = useState(1);
  const [step, setStep] = useState(1);
  const [loadingImages, setLoadingImages] = useState(true);
  const [imagesUnavailable, setImagesUnavailable] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const examImagesContainerRef = useRef();

  useDisableBodyScroll(examImagesModal);

  useEffect(() => {
    if (images && images.length > 0) {
      setLoadingImages(false);
      setImagesUnavailable(false);
    } else {
      const timeoutId = setTimeout(() => {
        setLoadingImages(false);
        setImagesUnavailable(true);
      }, 116000);

      return () => clearTimeout(timeoutId);
    }
  }, [images]);

  const imageLength = useMemo(() => {
    if (images?.length) {
      setStep(Math.ceil(images.length / 2));
      return images.length;
    }
  }, [images]);

  const onChangeInputRange = (event) => {
    const value = event.target.value;
    if (value) {
      setStep(parseInt(value, 10));
    }
  };

  const onClickImageGalleryNavigation = (newStep) => {
    setStep(Math.max(1, Math.min(imageLength, newStep)));
  };

  const handleOpenModal = () => {
    setExamImagesModal(true);
  };

  const handleCloseModal = () => {
    setExamImagesModal(false);
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const zoomIntensity = 0.1;
    const newScale = Math.min(Math.max(1, scale + (e.deltaY < 0 ? zoomIntensity : -zoomIntensity)), 5);
    setScale(newScale);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setOffset((prevOffset) => ({
      x: prevOffset.x + (e.clientX - dragStart.x),
      y: prevOffset.y + (e.clientY - dragStart.y),
    }));
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <NewExamImagesGalleryStyle>
      {loadingImages ? (
        <div className="loading-section">
          <LoadingSection message={translate('loadingExamImages', language)} />
        </div>
      ) : imagesUnavailable ? (
        <div className="no-image-photo">
          <NoPhotoIcon />
        </div>
      ) : (
        <>
          <div className="input-range-container">
            <input
              className="slider"
              type="range"
              value={step}
              min={1}
              max={imageLength}
              step={1}
              onChange={onChangeInputRange}
            />
            <p className="input-range-value">{step}</p>
          </div>

          <div className="exam-images-container">
            {images.map((image, index) => (
              <div
                key={image?.id}
                className={`single-exam-image${index === step - 1 ? ' show' : ''}`}
              >
                <div className="exam-image-wrapper">
                  {image?.original && (
                    <img
                      className="exam-image-file"
                      src={image?.original}
                      alt="Original"
                    />
                  )}
                </div>
                <div className="exam-image-wrapper">
                  {image?.classified && (
                    <img
                      className="exam-image-file"
                      src={image?.classified}
                      alt="Classified"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            className="image-gallery-navigation-previous"
            type="button"
            disabled={step <= 1}
            onClick={() => onClickImageGalleryNavigation(step - 1)}
          >
            <ChevronIcon />
          </button>
          <button
            className="image-gallery-navigation-next"
            type="button"
            disabled={step >= imageLength}
            onClick={() => onClickImageGalleryNavigation(step + 1)}
          >
            <ChevronIcon />
          </button>
          <button
            className="show-modal"
            type="button"
            aria-label="Show modal"
            onClick={handleOpenModal}
          >
            <ZoomIcon />
          </button>

          {examImagesModal && images.length ? (
            <Modal
              overlay={`${functions.rgba(colors.black, 0.7)}`}
              theme="dark"
              fullScreen
              handleClose={handleCloseModal}
            >
              <div className="exam-image-modal-container">
                <div className="modal-content">
                  <div className="input-range-container">
                    <input
                      className="slider"
                      type="range"
                      value={step}
                      min={1}
                      max={imageLength}
                      step={1}
                      onChange={onChangeInputRange}
                    />
                    <p className="input-range-value">{step}</p>
                  </div>

                  <div
                    ref={examImagesContainerRef}
                    className="exam-images-wrapper"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                      cursor: isDragging ? 'grabbing' : 'grab',
                      overflow: 'hidden',
                    }}
                  >
                    <div className="exam-images-container">
                      {images.map((image, index) => (
                        <div
                          key={image?.id}
                          className={`single-exam-image${index === step - 1 ? ' show' : ''}`}
                          style={{
                            transform: `scale(${scale}) translate(${offset.x}px, ${offset.y}px)`,
                            transformOrigin: 'center center',
                          }}
                        >
                          <div className="exam-image-wrapper">
                            {image?.original && (
                              <img
                                className="exam-image-file"
                                src={image?.original}
                                alt="Original"
                              />
                            )}
                          </div>
                          <div className="exam-image-wrapper">
                            {image?.classified && (
                              <img
                                className="exam-image-file"
                                src={image?.classified}
                                alt="Classified"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    className="image-gallery-navigation-previous"
                    type="button"
                    disabled={step <= 1}
                    onClick={() => onClickImageGalleryNavigation(step - 1)}
                  >
                    <ChevronIcon />
                  </button>
                  <button
                    className="image-gallery-navigation-next"
                    type="button"
                    disabled={step >= imageLength}
                    onClick={() => onClickImageGalleryNavigation(step + 1)}
                  >
                    <ChevronIcon />
                  </button>

                  <div className="image-gallery-scale-actions">
                    <button
                      className="image-gallery-scale-positive"
                      type="button"
                      disabled={scale >= 5}
                      onClick={() => setScale(Math.min(scale + 0.1, 5))}
                    >
                      <PositiveIcon />
                    </button>
                    <button
                      className="image-gallery-scale-negative"
                      type="button"
                      disabled={scale <= 1}
                      onClick={() => setScale(Math.max(scale - 0.1, 1))}
                    >
                      <NegativeIcon />
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          ) : null}
        </>
      )}
    </NewExamImagesGalleryStyle>
  );
};

NewExamImagesGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      original: PropTypes.string,
      classified: PropTypes.string,
    })
  ),
};

NewExamImagesGallery.defaultProps = {
  images: [],
};

export default NewExamImagesGallery;
